import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getApiError } from "@/lib/api";
import type { Media } from "@/types";
import { notify } from "@/utils";

import {
  useDeleteGroup,
  useFetchGroupDetails,
  useFetchGroupParticipants,
  useLeaveGroup,
  useUpdateGroup,
} from "../../services/group";
import type { GroupPrivacy } from "../creating-groups/types";
import GroupEditMainView from "./GroupEditMainView";
import GroupLeaveModal from "./GroupLeaveModal";
import GroupManagersPage from "./GroupManagersPage";
import GroupMembersEditPage from "./GroupMembersEditPage";
import GroupTypeEditPage from "./GroupTypeEditPage";
import { GroupEditSkeleton } from "./GroupUserListSkeleton";

type EditView = "main" | "type" | "managers" | "members";

type GroupEditPageProps = {
  groupId: number;
  fallbackName?: string;
  onBack: () => void;
  onDeleted?: () => void;
};

export default function GroupEditPage({
  groupId,
  fallbackName,
  onBack,
  onDeleted,
}: GroupEditPageProps) {
  const { t } = useTranslation("messenger.user");
  const [view, setView] = useState<EditView>("main");
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const { data: groupDetails, isLoading: isGroupLoading } =
    useFetchGroupDetails(groupId);
  const { data: participants, isLoading: isParticipantsLoading } =
    useFetchGroupParticipants(groupId);
  const updateGroup = useUpdateGroup(groupId);

  const deleteGroup = useDeleteGroup(groupId);
  const leaveGroup = useLeaveGroup(groupId);

  const [name, setName] = useState<string | null>(null);
  const [type, setType] = useState<GroupPrivacy>("private");
  const [imageMedia, setImageMedia] = useState<Media | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);

  useEffect(() => {
    if (groupDetails) {
      setName(groupDetails.name);
      setType(groupDetails.type);
      setImageMedia(null);
      setImageRemoved(false);
    }
  }, [groupDetails]);

  const displayName = name ?? groupDetails?.name ?? fallbackName ?? "";
  const memberCount = participants?.total ?? participants?.data?.length ?? 0;
  const managerCount = groupDetails?.owner ? 1 : 0;

  const inviteUrl = groupDetails?.invite_token
    ? `${window.location.origin}/messenger/groups/join/${groupDetails.invite_token}`
    : "";

  const handleCopyInviteLink = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl);
    notify.success(t("Link copied to clipboard"));
  };

  const buildUpdatePayload = (overrides?: {
    name?: string;
    type?: GroupPrivacy;
  }) => {
    const payload: {
      name: string;
      type: GroupPrivacy;
      image?: string | null;
    } = {
      name: overrides?.name ?? name ?? groupDetails?.name ?? fallbackName ?? "",
      type: overrides?.type ?? type,
    };

    if (imageRemoved) {
      payload.image = null;
    } else if (imageMedia) {
      payload.image = imageMedia.uuid;
    }

    return payload;
  };

  const initializedGroupId = useRef<number | null>(null);

  useEffect(() => {
    if (!groupDetails || initializedGroupId.current === groupId) return;

    setName(groupDetails.name ?? "");
    setType(groupDetails.type);
    setImageMedia(null);
    setImageRemoved(false);

    initializedGroupId.current = groupId;
  }, [groupDetails, groupId]);

  const handleSave = (overrides?: { name?: string; type?: GroupPrivacy }) => {
    const payload = buildUpdatePayload(overrides);
    updateGroup.mutate(payload, {
      onSuccess: () => {
        setName(payload.name);
        setType(payload.type);
        notify.success(t("Group updated successfully"));
        if (view !== "main") setView("main");
        else onBack();
      },
      onError: (error) => {
        notify.error(getApiError(error) || t("Failed to update group"));
      },
    });
  };

  const handleDeleteLeave = () => {
    if (groupDetails?.can_delete) {
      deleteGroup.mutate(undefined, {
        onSuccess: () => {
          notify.success(t("Group deleted successfully"));
          setShowLeaveModal(false);
          onDeleted?.();
          onBack();
        },
        onError: (error) => {
          notify.error(getApiError(error) || t("Failed to delete group"));
        },
      });
      return;
    }

    if (!groupDetails?.can_leave) return;

    leaveGroup.mutate(undefined, {
      onSuccess: () => {
        notify.success(t("Left group successfully"));
        setShowLeaveModal(false);
        onDeleted?.();
        onBack();
      },
      onError: (error) => {
        notify.error(getApiError(error) || t("Failed to leave group"));
      },
    });
  };

  if (isGroupLoading && view === "main") {
    return <GroupEditSkeleton />;
  }

  if (view === "type") {
    return (
      <GroupTypeEditPage
        type={groupDetails?.type ?? type}
        onBack={() => setView("main")}
        onConfirm={(nextType) => {
          if (!name?.trim()) return;
          handleSave({ name: name.trim(), type: nextType });
        }}
        isSaving={updateGroup.isPending}
        inviteUrl={inviteUrl}
        onCopyLink={handleCopyInviteLink}
      />
    );
  }

  if (view === "managers") {
    return (
      <GroupManagersPage
        groupDetails={groupDetails}
        isLoading={isGroupLoading}
        onBack={() => setView("main")}
      />
    );
  }

  if (view === "members") {
    return (
      <GroupMembersEditPage
        groupId={groupId}
        groupDetails={groupDetails}
        isLoading={isParticipantsLoading}
        onBack={() => setView("main")}
        inviteUrl={inviteUrl}
        onCopyLink={handleCopyInviteLink}
      />
    );
  }

  return (
    <>
      <GroupEditMainView
        name={displayName}
        groupDetails={groupDetails}
        imageMedia={imageMedia}
        imageRemoved={imageRemoved}
        managerCount={managerCount}
        memberCount={memberCount}
        isSaving={updateGroup.isPending}
        onNameChange={setName}
        onImageUpload={(media) => {
          setImageMedia(media);
          setImageRemoved(false);
        }}
        onImageDelete={() => {
          setImageMedia(null);
          setImageRemoved(true);
        }}
        onBack={onBack}
        onConfirm={() => handleSave()}
        onTypeClick={() => setView("type")}
        onManagersClick={() => setView("managers")}
        onMembersClick={() => setView("members")}
        onDeleteLeave={() => setShowLeaveModal(true)}
      />
      <GroupLeaveModal
        open={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onConfirm={handleDeleteLeave}
        isLoading={deleteGroup.isPending || leaveGroup.isPending}
        isDelete={!!groupDetails?.can_delete}
      />
    </>
  );
}
